import styled from 'styled-components'

const getSpacingValue = (
  value: string | number | undefined
): string | undefined => {
  if (value === undefined) return undefined
  if (typeof value === 'number') return `${value}px`
  return value
}

export interface StyledBoxProps {
  $display?: string
  $flexDirection?: string
  $flexWrap?: string
  $alignItems?: string
  $justifyContent?: string
  $gap?: string | number
  $flex?: string | number
  $flexShrink?: number
  $margin?: string | number
  $marginTop?: string | number
  $marginRight?: string | number
  $marginBottom?: string | number
  $marginLeft?: string | number
  $marginX?: string | number
  $marginY?: string | number
  $padding?: string | number
  $paddingTop?: string | number
  $paddingRight?: string | number
  $paddingBottom?: string | number
  $paddingLeft?: string | number
  $paddingX?: string | number
  $paddingY?: string | number
  $width?: string | number
  $height?: string | number
  $minWidth?: string | number
  $minHeight?: string | number
  $maxWidth?: string | number
  $maxHeight?: string | number
  $position?: string
  $top?: string | number
  $right?: string | number
  $bottom?: string | number
  $left?: string | number
  $zIndex?: number
  $backgroundColor?: string
  $backgroundImage?: string
  $backgroundSize?: string
  $backgroundPosition?: string
  $backgroundRepeat?: string
  $border?: string
  $borderRadius?: string | number
  $borderColor?: string
  $borderWidth?: string | number
  $borderStyle?: string
  $textAlign?: string
  $color?: string
  $overflow?: string
  $cursor?: string
  $opacity?: number
  $boxShadow?: string
  $transform?: string
}

export const StyledBox = styled.div<StyledBoxProps>`
  ${({ $display }) => $display && `display: ${$display};`}

  /* Margin */
  ${({ $margin }) =>
    $margin !== undefined && `margin: ${getSpacingValue($margin)};`}
  ${({ $marginTop, $marginY }) => {
    const value = $marginTop ?? $marginY
    return value !== undefined && `margin-top: ${getSpacingValue(value)};`
  }}
  ${({ $marginRight, $marginX }) => {
    const value = $marginRight ?? $marginX
    return value !== undefined && `margin-right: ${getSpacingValue(value)};`
  }}
  ${({ $marginBottom, $marginY }) => {
    const value = $marginBottom ?? $marginY
    return value !== undefined && `margin-bottom: ${getSpacingValue(value)};`
  }}
  ${({ $marginLeft, $marginX }) => {
    const value = $marginLeft ?? $marginX
    return value !== undefined && `margin-left: ${getSpacingValue(value)};`
  }}
  
  /* Padding */
  ${({ $padding }) =>
    $padding !== undefined && `padding: ${getSpacingValue($padding)};`}
  ${({ $paddingTop, $paddingY }) => {
    const value = $paddingTop ?? $paddingY
    return value !== undefined && `padding-top: ${getSpacingValue(value)};`
  }}
  ${({ $paddingRight, $paddingX }) => {
    const value = $paddingRight ?? $paddingX
    return value !== undefined && `padding-right: ${getSpacingValue(value)};`
  }}
  ${({ $paddingBottom, $paddingY }) => {
    const value = $paddingBottom ?? $paddingY
    return value !== undefined && `padding-bottom: ${getSpacingValue(value)};`
  }}
  ${({ $paddingLeft, $paddingX }) => {
    const value = $paddingLeft ?? $paddingX
    return value !== undefined && `padding-left: ${getSpacingValue(value)};`
  }}
  
  /* Sizing */
  ${({ $width }) =>
    $width !== undefined && `width: ${getSpacingValue($width)};`}
  ${({ $height }) =>
    $height !== undefined && `height: ${getSpacingValue($height)};`}
  ${({ $minWidth }) =>
    $minWidth !== undefined && `min-width: ${getSpacingValue($minWidth)};`}
  ${({ $minHeight }) =>
    $minHeight !== undefined && `min-height: ${getSpacingValue($minHeight)};`}
  ${({ $maxWidth }) =>
    $maxWidth !== undefined && `max-width: ${getSpacingValue($maxWidth)};`}
  ${({ $maxHeight }) =>
    $maxHeight !== undefined && `max-height: ${getSpacingValue($maxHeight)};`}
  
  /* Flexbox */
  ${({ $flex }) => $flex && `flex: ${$flex};`}
  ${({ $flexShrink }) =>
    $flexShrink !== undefined && `flex-shrink: ${$flexShrink};`}
  ${({ $flexDirection }) =>
    $flexDirection && `flex-direction: ${$flexDirection};`}
  ${({ $flexWrap }) => $flexWrap && `flex-wrap: ${$flexWrap};`}
  ${({ $alignItems }) => $alignItems && `align-items: ${$alignItems};`}
  ${({ $justifyContent }) =>
    $justifyContent && `justify-content: ${$justifyContent};`}
  ${({ $gap }) => $gap !== undefined && `gap: ${getSpacingValue($gap)};`}
  
  /* Position */
  ${({ $position }) => $position && `position: ${$position};`}
  ${({ $top }) => $top !== undefined && `top: ${getSpacingValue($top)};`}
  ${({ $right }) =>
    $right !== undefined && `right: ${getSpacingValue($right)};`}
  ${({ $bottom }) =>
    $bottom !== undefined && `bottom: ${getSpacingValue($bottom)};`}
  ${({ $left }) => $left !== undefined && `left: ${getSpacingValue($left)};`}
  ${({ $zIndex }) => $zIndex !== undefined && `z-index: ${$zIndex};`}
  
  /* Background */
  ${({ $backgroundColor }) =>
    $backgroundColor && `background-color: ${$backgroundColor};`}
  ${({ $backgroundImage }) =>
    $backgroundImage && `background-image: ${$backgroundImage};`}
  ${({ $backgroundSize }) =>
    $backgroundSize && `background-size: ${$backgroundSize};`}
  ${({ $backgroundPosition }) =>
    $backgroundPosition && `background-position: ${$backgroundPosition};`}
  ${({ $backgroundRepeat }) =>
    $backgroundRepeat && `background-repeat: ${$backgroundRepeat};`}
  
  /* Border */
  ${({ $border }) => $border && `border: ${$border};`}
  ${({ $borderRadius }) =>
    $borderRadius !== undefined &&
    `border-radius: ${getSpacingValue($borderRadius)};`}
  ${({ $borderColor }) => $borderColor && `border-color: ${$borderColor};`}
  ${({ $borderWidth }) =>
    $borderWidth !== undefined &&
    `border-width: ${getSpacingValue($borderWidth)};`}
  ${({ $borderStyle }) => $borderStyle && `border-style: ${$borderStyle};`}
  
  /* Typography */
  ${({ $textAlign }) => $textAlign && `text-align: ${$textAlign};`}
  ${({ $color }) => $color && `color: ${$color};`}
  
  /* Other */
  ${({ $overflow }) => $overflow && `overflow: ${$overflow};`}
  ${({ $cursor }) => $cursor && `cursor: ${$cursor};`}
  ${({ $opacity }) => $opacity !== undefined && `opacity: ${$opacity};`}
  ${({ $boxShadow }) => $boxShadow && `box-shadow: ${$boxShadow};`}
  ${({ $transform }) => $transform && `transform: ${$transform};`}
`
